import Fab from "@mui/material/Fab";
import { observer } from "mobx-react-lite";
import { useCallback, useContext, useState } from "react";
import { AppContext } from "../../contexts/AppContext";
import { IMapWeatherLayer, weatherLayers } from "../../services/MapService";
import styled from "@mui/material/styles/styled";
import Box from "@mui/material/Box";
import WbSunny from "@mui/icons-material/WbSunny";
import Slider from "@mui/material/Slider";
import dayjs from "dayjs";
import { useIntl } from 'react-intl';
import roundToNearest30Minutes from "../../utils/roundToNearest30Minutes";
import { PlannedEffort, SectionData } from "../../services/PlansService";
import { round } from "../../utils/UnitsUtils";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import useRestricted from "../../hooks/useRestricted";
import { Permission } from "../../services/UserService";
import PermissionDialog from "../common/PermissionDialog";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { debounce } from "lodash";
import posthog from "posthog-js";

const WeatherControlFab = styled(Fab)(({ theme }) => ({
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(21),
}));

const WeatherBox = styled(Box)(({ theme }) => ({
    top: theme.spacing(7),
    right: theme.spacing(21),
}));

const getLatLngForTime = (date: dayjs.Dayjs, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs, plannedEffort: PlannedEffort[], sectionsData: SectionData[], distancePoints: number[], latLngPoints: [number, number][]) => {

    if (date.isBefore(startDate)) {
        return latLngPoints[0]; // Return the first point if the date is outside the range
    }

    if (date.isAfter(endDate)) {
        return latLngPoints[latLngPoints.length - 1]; // Return the last point if the date is outside the range
    }

    let accEffortTime = 0;
    for (let i = 0; i < plannedEffort.length; i++) {
        const effort = plannedEffort[i];
        const sectionData = sectionsData[i];
        const sectionStartTime = startDate.add(accEffortTime, 'minute');
        const sectionEndTime = sectionStartTime.add(effort.time, 'minute');

        if ((date.isBefore(sectionEndTime) && date.isAfter(sectionStartTime)) || date.isSame(sectionStartTime)) {
            const progress = (date.diff(sectionStartTime, 'minute') / (sectionEndTime.diff(sectionStartTime, 'minute') || 1));

            const distanceAtProgress = sectionData.startDistance + (sectionData.type === "section" ? (sectionData.distance * progress) : 0);
            for (let j = 0; j < distancePoints.length; j++) {
                if (distancePoints[j] >= distanceAtProgress) {
                    return latLngPoints[j];
                }
            }
        }
        accEffortTime += effort.time;
    }
    return latLngPoints[latLngPoints.length - 1];
}

export const WeatherControl = observer(() => {

    const { mapService, plansService, userService } = useContext(AppContext);

    const distanceUnits = userService.user?.distanceUnits || "km";
    const temperatureUnits = userService.user?.temperatureUnits || "C";

    const permission = useRestricted();
    const intl = useIntl();

    const [open, setOpen] = useState<boolean>(false);
    const [openPermissionDialog, setOpenPermissionDialog] = useState<boolean>(false);
    const [openDatesDialog, setOpenDatesDialog] = useState<boolean>(false);
    const [datesDialogShown, setDatesDialogShown] = useState<boolean>(false);

    const [position, setPosition] = useState<number>(0);

    let isRealDates = true;

    const planDate = dayjs(plansService.plan?.date);

    const today = dayjs().startOf('day').add(planDate.hour(), 'hour').add(planDate.minute(), 'minute');
    const fourDaysBefore = planDate.subtract(4, 'day');
    const oneDayAfter = planDate.add(1, 'day');

    const debouncedGetWeatherData = useCallback(
        debounce((latLng: [number, number]) => {
            const weatherData = mapService.getWeatherData(latLng);

            mapService.addPopUp(latLng, `
            <b>${dayjs(weatherData.date).format("lll")}</b><br />
            <b>${intl.formatMessage({ id: "weather.temperature" })}: </b>${round((temperatureUnits === "C" ? weatherData.temperature?.value : weatherData.temperature?.valueImperial) || 0, 0)} °${temperatureUnits}</b><br />
            <b>${intl.formatMessage({ id: "weather.wind" })}: </b>${round((distanceUnits === "km" ? weatherData.wind?.speedKilometersPerHour : weatherData.wind?.speedMilesPerHour) || 0, 1)} ${intl.formatMessage({ id: distanceUnits })}/h ${intl.formatMessage({ id: "weather.windComingFrom" })} ${weatherData.wind?.compassDirection}<br />
            <b>${intl.formatMessage({ id: "weather.precipitation" })}: </b>${round(weatherData.precipitation?.value || 0, 2)} mm/h<br />
            <b>${intl.formatMessage({ id: "weather.pressure" })}: </b>${round(weatherData.pressure?.value || 0, 0)} hPa<br />
        `,
                "main-weather-popup");
        }, 10, { leading: false, trailing: true }),
        [mapService, temperatureUnits, distanceUnits, intl]
    );

    if (today.isBefore(fourDaysBefore) || today.isAfter(oneDayAfter)) {

        isRealDates = false;
    }


    const handleWeatherToggle = () => {

        posthog.capture('weather_control_toggled', { open: !open });
        if (permission === Permission.loading) {
            return;
        }

        if (permission !== Permission.free) {


            if (!isRealDates && !datesDialogShown) {
                setDatesDialogShown(true);
                setOpenDatesDialog(true);
            }

            if (!open || !mapService.currentWeatherMapLayers || mapService.currentWeatherMapLayers.length === 0) {
                mapService.initWeatherLayers();
            }
            setOpen(!open);
        } else {
            setOpenPermissionDialog(true);
        }
    }

    const handleWeatherChange = (weatherLayer: IMapWeatherLayer) => {
        posthog.capture('weather_layer_toggled', { layer: weatherLayer.id });
        if (!mapService.currentWeatherMapLayers?.some(l => l.id === weatherLayer.id)) {
            mapService.setWeatherLayer(weatherLayer);
        } else {
            mapService.unsetWeatherLayer(weatherLayer);
        }

        if (!mapService.currentWeatherMapLayers || mapService.currentWeatherMapLayers.length === 0) {
            mapService.removeAllPopups();
            setPosition(0);
        }
    }

    if (!plansService.plan) {
        return null;
    }

    const totalTime = plansService.plan.plannedEffort.reduce((acc, effort) => acc + (effort?.time ?? 0), 0);
    const startDate = roundToNearest30Minutes(dayjs(planDate), "floor").subtract(30, 'minute');
    const endDate = roundToNearest30Minutes(dayjs(planDate).add(totalTime, 'minute'), "ceil").add(30, 'minute');

    const startDateToday = roundToNearest30Minutes(dayjs(today), "floor").subtract(30, 'minute');
    const endDateToday = roundToNearest30Minutes(dayjs(today).add(totalTime, 'minute'), "ceil").add(30, 'minute');

    const handleTimeChange = (value: number) => {
        setPosition(value);
        mapService.setWeatherTime(dayjs(isRealDates ? startDate : startDateToday).add(value, 'minute').toDate().getTime() / 1000);

        const latLngForTime = getLatLngForTime(
            dayjs(startDate).add(value, 'minute'),
            dayjs(plansService.plan?.date),
            dayjs(plansService.plan?.date).add(totalTime, 'minute'),
            plansService.plan?.plannedEffort || [],
            plansService.sectionsData,
            plansService.distancePoints,
            plansService.latlng
        );

        debouncedGetWeatherData(latLngForTime);
    }

    // Weather layer names
    const getWeatherLayerName = (id: string) => {
        switch (id) {
            case 'temperature':
                return intl.formatMessage({ id: "weather.temperature" });
            case 'precipitation':
                return intl.formatMessage({ id: "weather.precipitation" });
            case 'pressure':
                return intl.formatMessage({ id: "weather.pressure" });
            case 'wind':
                return intl.formatMessage({ id: "weather.wind" });
            case 'radar':
                return intl.formatMessage({ id: "weather.radar" });
            default:
                return id;
        }
    };

    return (
        <>
            <WeatherControlFab
                size="small"
                onClick={handleWeatherToggle}
            >
                <WbSunny />
            </WeatherControlFab>
            {open && <WeatherBox
                display="flex"
                position="absolute"
                flexDirection="column"
                bgcolor="background.paper"
                borderRadius={1}
                boxShadow={3}
                p={2}
                width="200px"
            >
                <Box mb={2}>
                    {weatherLayers
                        .filter(layer => ['temperature', 'precipitation', 'pressure', 'radar'].includes(layer.id))
                        .map((weatherLayer) => (
                            <FormControlLabel
                                key={`${weatherLayer.id}-${mapService.currentWeatherMapLayers?.some(l => l.id === weatherLayer.id)}`}
                                control={
                                    <Radio
                                        size="small"
                                        checked={mapService.currentWeatherMapLayers?.some(l => l.id === weatherLayer.id)}
                                        onClick={() => handleWeatherChange(weatherLayer)}
                                        disabled={mapService.weatherLayersLoaded[weatherLayer.id] === false}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <weatherLayer.icon fontSize="small" />
                                        <Typography variant="body2">
                                            {getWeatherLayerName(weatherLayer.id)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))
                    }
                </Box>

                {weatherLayers
                    .filter(layer => layer.id === 'wind')
                    .map((windLayer) => (
                        <FormControlLabel
                            key={windLayer.id}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={mapService.currentWeatherMapLayers?.some(l => l.id === windLayer.id)}
                                    onChange={() => handleWeatherChange(windLayer)}
                                    disabled={mapService.weatherLayersLoaded[windLayer.id] === false}
                                    color="primary"
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <windLayer.icon fontSize="small" />
                                    <Typography variant="body2">{getWeatherLayerName(windLayer.id)}</Typography>
                                </Box>
                            }
                        />
                    ))
                }
            </WeatherBox>}

            {!!mapService.currentWeatherMapLayers?.length && Object.values(mapService.weatherLayersLoaded).every(v => !!v) && <Box
                position="absolute"
                bottom={16}
                left={16}
                width="calc(100% - 32px)"
                flexDirection="column"
                display="flex"
                justifyContent="center"
                zIndex={10}
                bgcolor="rgba(255, 255, 255, 0.5)"
                borderRadius={2}
                boxShadow={3}
                paddingX={1}
                paddingTop={1}
                paddingBottom={0}
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                >
                    <Typography variant="caption">
                        {dayjs(isRealDates ? startDate : startDateToday).locale(intl.locale).format("lll")}
                    </Typography>
                    <Typography variant="caption">
                        {dayjs(isRealDates ? endDate : endDateToday).locale(intl.locale).format("lll")}
                    </Typography>
                </Box>
                <Slider
                    aria-label="time-indicator"
                    size="small"
                    value={position}
                    min={0}
                    step={5}
                    max={(isRealDates ? endDate : endDateToday).diff(isRealDates ? startDate : startDateToday, 'minute')}
                    onChange={(_, value) => handleTimeChange(+value)}
                    sx={(t) => ({
                        color: 'rgba(0,0,0,0.87)',
                        height: 4,
                        '& .MuiSlider-thumb': {
                            width: 8,
                            height: 8,
                            transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                            '&::before': {
                                boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
                            },
                            '&:hover, &.Mui-focusVisible': {
                                boxShadow: `0px 0px 0px 8px ${'rgb(0 0 0 / 16%)'}`,
                                ...t.applyStyles('dark', {
                                    boxShadow: `0px 0px 0px 8px ${'rgb(255 255 255 / 16%)'}`,
                                }),
                            },
                            '&.Mui-active': {
                                width: 20,
                                height: 20,
                            },
                        },
                        '& .MuiSlider-rail': {
                            opacity: 0.28,
                        },
                        ...t.applyStyles('dark', {
                            color: '#fff',
                        }),
                    })}
                />
            </Box>}

            <PermissionDialog
                open={openPermissionDialog}
                onClose={() => setOpenPermissionDialog(false)}
                title={intl.formatMessage({ id: "permission.weatherTitle" })}
                text={intl.formatMessage({ id: "permission.weatherText" })}
                video="/videos/weather.mp4"
            />

            <Dialog
                open={openDatesDialog}
                onClose={() => setOpenDatesDialog(false)}
                maxWidth="sm"
            >
                <DialogTitle>
                    {intl.formatMessage({ id: "weather.datesTitle" })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {intl.formatMessage({ id: "weather.datesText" })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDatesDialog(false)}>
                        {intl.formatMessage({ id: "action.done" })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});